import classNames from "classnames";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import ReactDOM from "react-dom";
import Spinner from "rivals/components/shared/Spinner";
import { PAYPAL_ENVS, QA_DATA_ATTRIBUTE_NAME } from "rivals/shared/constants";
import { PayPalAuthorizationData } from "rivals/shared/interfaces/User";
import { getPaypalEnvSafe } from "rivals/shared/utils/paypal";
import { setData } from "rivals/shared/utils/qa";
import styles from "./index.module.scss";
import strings from "./strings";
import {
  OwnProps,
  PayPalAuthorizeAction,
  PayPalShippingAction,
  PayPalShippingData,
  PayPalWindow
} from "./types";

const { scriptUrl } = strings;
const philterContainer = "paypal-button-container";
const philterIframe = "paypal-button-iframe";

const PayPalCheckout: FunctionComponent<OwnProps> = ({
  disabled = false,
  isLoading = false,
  readOnly = false,
  onApprovePayment,
  onCancelPayment,
  onError,
  size = "responsive",
  transactionId = ""
}) => {
  const payPalContainerRef = useRef<HTMLDivElement | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.onload = (): void => setIsScriptLoaded(true);

    const scriptExists = window.document.querySelector(`script[src=scriptUrl]`);

    if (!scriptExists) {
      document.body.appendChild(script);
    }
    return (): void => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (isScriptLoaded && !isLoading) {
      const payPalIframe = payPalContainerRef?.current?.querySelector(
        "iframe.zoid-component-frame"
      );
      payPalIframe?.setAttribute(QA_DATA_ATTRIBUTE_NAME, philterIframe);
    }
  }, [isScriptLoaded, isLoading]);

  const handleAuthorize = (
    data: PayPalAuthorizationData,
    action: PayPalAuthorizeAction
  ): void => {
    if (onApprovePayment) {
      onApprovePayment(data, action);
    }
  };

  const handlePayment = useCallback((): string => transactionId || "", [
    transactionId
  ]);

  if (!isScriptLoaded || isLoading) {
    return <Spinner />;
  }

  const { paypal } = window as PayPalWindow;
  const PayPalButton = paypal?.Button.driver("react", {
    React,
    ReactDOM
  });

  if (
    !PayPalButton ||
    !PAYPAL_ENVS.includes(process.env.NEXT_PUBLIC_PAYPAL_ENV || "")
  ) {
    return null;
  }

  return (
    <div
      className={styles.payPalContainer}
      ref={payPalContainerRef}
      {...setData(philterContainer)}
    >
      {(disabled || readOnly) && (
        <div
          className={classNames(styles.disabledOverlay, {
            [styles.readOnly]: readOnly
          })}
        />
      )}
      <PayPalButton
        commit
        env={getPaypalEnvSafe() || "sandbox"}
        funding={{ disallowed: [paypal?.FUNDING?.CREDIT] }}
        onAuthorize={handleAuthorize}
        onCancel={onCancelPayment}
        onError={onError}
        onShippingChange={(
          _: PayPalShippingData,
          action: PayPalShippingAction
        ): void => action.resolve()}
        payment={handlePayment}
        style={{
          color: readOnly ? "black" : "gold",
          label: "paypal",
          shape: "pill",
          size,
          tagline: false
        }}
      />
    </div>
  );
};

export default PayPalCheckout;
